import { useRef, useState } from "react";
import "./ResetPassword.scss";
import { theme, Layout, Typography, Form, Input, Button, message } from "antd";
import { useNavigate, useParams } from "react-router";
import resetPasswordSchema from "../../validations/resetPasswordValidation";
import { createYupValidator } from "../../utils/validationHelper";
import { resetPassword } from "../../features/forgetPassword/resetPasswordActions";
import { useDispatch } from "react-redux";

const { Content } = Layout;

const validMessageTypes = ["success", "info", "warning", "error"];

export default function ResetPassword() {
  const { useToken } = theme;
  const { token } = useToken();
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();
  const openMessage = (type, text) => {
    messageApi.open({
      type: type,
      content: text,
      duration: 3,
    });
  };
  const { user_id, token: user_token } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setPassword = (params) => {
    setLoading(true);
    dispatch(resetPassword(params))
      ?.then((res) => {
        if (res?.type === "reset_password/fulfilled") {
          openMessage(
            "success",
            res?.payload?.message || "Password reset successfully!"
          );
          setTimeout(() => {
            setError(null);
            setLoading(false);
            navigate("/login");
          }, 1000);
        } else if (res?.type === "reset_password/rejected") {
          setLoading(false);

          // Check for specific error types from backend
          const hasPasswordFieldError =
            typeof res?.payload?.password === "string" ||
            Array.isArray(res?.payload?.password);
          const hasTokenError = res?.payload?.token;
          const hasUidError = res?.payload?.uid;
          const hasPasswordConfirmError = res?.payload?.password_confirm;

          if (hasTokenError || hasUidError) {
            // Invalid or expired reset link - user needs to request a new one
            setError(null);
            const linkError = hasTokenError || hasUidError;
            openMessage(
              "error",
              Array.isArray(linkError) ? linkError[0] : linkError
            );
          } else if (hasPasswordConfirmError) {
            // Backend-side password mismatch
            setError({ password_confirm: res?.payload?.password_confirm });
            formRef?.current?.setFields([
              {
                name: "password_confirm",
                errors: Array.isArray(res?.payload?.password_confirm)
                  ? res?.payload?.password_confirm
                  : [res?.payload?.password_confirm],
              },
            ]);
          } else if (hasPasswordFieldError) {
            // Handle password field validation errors
            setError({ password: res?.payload?.password });
            formRef?.current?.setFields([
              {
                name: "password",
                errors: Array.isArray(res?.payload?.password)
                  ? res?.payload?.password
                  : [res?.payload?.password],
              },
            ]);
            // Don't show a toast message since field error is displayed
          } else {
            // Handle all other errors (404, 500, 400 without field errors, etc.)
            setError(null);

            // Determine error message and type
            let errorMessage = "Failed to reset password.";
            let messageType = "error";

            if (res?.payload?.message) {
              errorMessage = res.payload.message;
            } else if (typeof res?.payload === "string") {
              errorMessage = res.payload;
            }

            // Use status from payload if valid, otherwise default to error
            if (res?.payload?.status) {
              const statusLower = res.payload.status.toLowerCase();
              if (validMessageTypes.includes(statusLower)) {
                messageType = statusLower;
              }
            }

            openMessage(messageType, errorMessage);
          }
        }
      })
      .catch(() => {
        setLoading(false);
        setError(null);
        openMessage("error", "An unexpected error occurred. Please try again.");
      });
  };

  return (
    <>
      {contextHolder}
      <Content
        className="resetPassword-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <div className="resetPassword-cont__wrapper">
          <Typography.Text
            className="resetPassword-cont__wrapper__title"
            style={{
              color: token?.Typography?.colorPrimaryText,
            }}>
            Reset Password
          </Typography.Text>
          <Form
            className="resetPassword-cont__wrapper__form"
            ref={formRef}
            layout="vertical"
            onFinish={(values) => {
              setPassword({
                ...values,
                token: user_token,
                uid: user_id,
              });
            }}>
            <Form.Item
              className="resetPassword-cont__wrapper__form__formItem"
              name="password"
              hasFeedback
              rules={[
                {
                  required: true,
                  validator: createYupValidator(
                    resetPasswordSchema,
                    "password"
                  ),
                },
              ]}
              label={<Typography.Text>Password</Typography.Text>}
              colon={false}>
              <Input.Password
                placeholder="Password"
                size="large"
                autoComplete="new-password" // Enables browser to autofill new password securely
                onChange={() => {
                  if (error?.password) {
                    setError(error?.password_confirm ? { password_confirm: error.password_confirm } : null);
                  }
                }}
              />
            </Form.Item>
            <div>
              <Form.Item
                className="resetPassword-cont__wrapper__form__formItem"
                name="password_confirm"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  {
                    required: true,
                    validator: async (_, value) => {
                      try {
                        // Pass all form values to enable cross-field validation (Yup.ref)
                        const formValues = formRef?.current?.getFieldsValue();
                        await resetPasswordSchema?.validateAt(
                          "password_confirm",
                          formValues
                        );
                        return Promise.resolve();
                      } catch (error) {
                        return Promise.reject(error?.message);
                      }
                    },
                  },
                ]}
                label={<Typography.Text>Confirm Password</Typography.Text>}
                colon={false}>
                <Input.Password
                  placeholder="Confirm Password"
                  size="large"
                  autoComplete="new-password" // Enables browser to autofill new password securely
                  onChange={() => {
                    if (error?.password_confirm) {
                      setError(error?.password ? { password: error.password } : null);
                    }
                  }}
                />
              </Form.Item>
            </div>
            <Button
              type="primary"
              size="large"
              loading={loading}
              disabled={!!(error?.password || error?.password_confirm)}
              onClick={() => {
                formRef?.current?.submit();
              }}>
              Set Password
            </Button>
          </Form>
        </div>
      </Content>
    </>
  );
}
